/**
 * 性能优化工具
 * 提供图片懒加载、资源预加载等功能
 */

// 图片懒加载Hook
import { useEffect, useRef, useState } from 'react';

export const useLazyImage = (src, options = {}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [imageRef, setImageRef] = useState();
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    let observer;
    
    if (imageRef && imageSrc !== src) {
      if (IntersectionObserver) {
        observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setImageSrc(src);
                setIsInView(true);
                observer.unobserve(imageRef);
              }
            });
          },
          {
            threshold: 0.1,
            rootMargin: '50px',
            ...options
          }
        );
        observer.observe(imageRef);
      } else {
        // 如果不支持 IntersectionObserver，直接加载图片
        setImageSrc(src);
        setIsInView(true);
      }
    }
    
    return () => {
      if (observer && observer.unobserve) {
        observer.unobserve(imageRef);
      }
    };
  }, [imageRef, src, imageSrc, options]);

  // 处理图片加载完成
  useEffect(() => {
    if (imageSrc) {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setIsLoaded(false);
      img.src = imageSrc;
    }
  }, [imageSrc]);

  return [setImageRef, imageSrc, isLoaded, isInView];
};

// 懒加载图片组件
export const LazyImage = ({ 
  src, 
  alt, 
  placeholder = '/images/placeholder.png',
  className = '',
  style = {},
  ...props 
}) => {
  const [setImageRef, imageSrc, isLoaded, isInView] = useLazyImage(src);
  
  return (
    <div 
      ref={setImageRef}
      className={`lazy-image-container ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      {/* 占位符 */}
      {!isInView && (
        <img
          src={placeholder}
          alt={alt}
          className="lazy-placeholder"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: 'blur(5px)',
            transition: 'filter 0.3s ease'
          }}
          {...props}
        />
      )}
      
      {/* 实际图片 */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`lazy-image ${isLoaded ? 'loaded' : 'loading'}`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            position: isInView ? 'static' : 'absolute',
            top: 0,
            left: 0
          }}
          {...props}
        />
      )}
      
      {/* 加载指示器 */}
      {isInView && !isLoaded && (
        <div 
          className="loading-indicator"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#6c757d'
          }}
        >
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">加载中...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// 资源预加载功能
export const preloadResources = (resources) => {
  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    
    if (resource.type === 'image') {
      link.as = 'image';
      link.href = resource.src;
    } else if (resource.type === 'script') {
      link.as = 'script';
      link.href = resource.src;
    } else if (resource.type === 'style') {
      link.as = 'style';
      link.href = resource.src;
    }
    
    if (resource.crossorigin) {
      link.crossOrigin = resource.crossorigin;
    }
    
    document.head.appendChild(link);
  });
};

// 关键资源预加载
export const preloadCriticalResources = () => {
  const criticalResources = [
    { type: 'image', src: '/images/logo.png' },
    { type: 'image', src: '/images/og-image.png' },
    // 可以根据需要添加更多关键资源
  ];
  
  preloadResources(criticalResources);
};

// 代码分割预加载Hook
export const useRoutePreload = (routeComponents) => {
  useEffect(() => {
    // 预加载路由组件
    const preloadRoutes = () => {
      Object.values(routeComponents).forEach(component => {
        if (typeof component === 'function') {
          // 预加载懒加载的组件
          component();
        }
      });
    };
    
    // 延迟预加载，避免阻塞初始渲染
    const timer = setTimeout(preloadRoutes, 2000);
    
    return () => clearTimeout(timer);
  }, [routeComponents]);
};

// 性能监控
export const performanceMonitor = {
  // 测量 LCP (Largest Contentful Paint)
  measureLCP: (callback) => {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        callback(lastEntry.startTime);
      });
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }
  },
  
  // 测量 FID (First Input Delay)
  measureFID: (callback) => {
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          callback(entry.processingStart - entry.startTime);
        });
      });
      observer.observe({ entryTypes: ['first-input'] });
    }
  },
  
  // 测量 CLS (Cumulative Layout Shift)
  measureCLS: (callback) => {
    if ('PerformanceObserver' in window) {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
            callback(clsValue);
          }
        });
      });
      observer.observe({ entryTypes: ['layout-shift'] });
    }
  }
};

// 内存优化
export const memoryOptimization = {
  // 清理事件监听器
  cleanupEventListeners: (element, events) => {
    events.forEach(({ type, handler }) => {
      element.removeEventListener(type, handler);
    });
  },
  
  // 防抖函数
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },
  
  // 节流函数
  throttle: (func, limit) => {
    let lastFunc;
    let lastRan;
    return function(...args) {
      if (!lastRan) {
        func.apply(this, args);
        lastRan = Date.now();
      } else {
        clearTimeout(lastFunc);
        lastFunc = setTimeout(() => {
          if ((Date.now() - lastRan) >= limit) {
            func.apply(this, args);
            lastRan = Date.now();
          }
        }, limit - (Date.now() - lastRan));
      }
    };
  }
};

export default {
  useLazyImage,
  LazyImage,
  preloadResources,
  preloadCriticalResources,
  useRoutePreload,
  performanceMonitor,
  memoryOptimization
};